export const MAX_PARSE_RESULT_IDS = 50;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type IdLineStatus = "ok" | "not-uuid" | "duplicate" | "over-limit";

export type IdLine = { number: number; id: string; status: IdLineStatus };

export type IdAnalysis = {
  lines: IdLine[];
  ids: string[];
  error: string | undefined;
  warning: string | undefined;
};

export function pluralise(count: number, noun: string): string {
  return `${count} ${noun}${count !== 1 ? "s" : ""}`;
}

function classifyLines(tokens: string[]): IdLine[] {
  const firstSeen = new Map<string, number>();
  let uniqueCount = 0;
  return tokens.map((id, index) => {
    const number = index + 1;
    if (firstSeen.has(id)) return { number, id, status: "duplicate" };
    firstSeen.set(id, number);
    uniqueCount += 1;
    if (!UUID_PATTERN.test(id)) return { number, id, status: "not-uuid" };
    if (uniqueCount > MAX_PARSE_RESULT_IDS) return { number, id, status: "over-limit" };
    return { number, id, status: "ok" };
  });
}

function firstIdLineProblem(
  lines: IdLine[],
  tokens: string[],
): Pick<IdAnalysis, "error" | "warning"> {
  const notUuid = lines.find(line => line.status === "not-uuid");
  if (notUuid) return { error: `Line ${notUuid.number} is not a UUID.`, warning: undefined };
  const over = lines.find(line => line.status === "over-limit");
  if (over) {
    return {
      error: `Paste at most ${MAX_PARSE_RESULT_IDS} IDs. Line ${over.number} and below are over the limit.`,
      warning: undefined,
    };
  }
  const duplicate = lines.find(line => line.status === "duplicate");
  if (!duplicate) return { error: undefined, warning: undefined };
  const original = tokens.indexOf(duplicate.id) + 1;
  const picked = new Set(tokens).size;
  return {
    error: undefined,
    warning: `Line ${duplicate.number} repeats line ${original} and is counted once. ${pluralise(picked, "document")} will be picked.`,
  };
}

export function analyseIds(text: string): IdAnalysis {
  const tokens = text.split(/[\s,]+/).filter(token => token !== "");
  const lines = classifyLines(tokens);
  return { lines, ids: [...new Set(tokens)], ...firstIdLineProblem(lines, tokens) };
}

/** The limit as a positive integer, undefined when empty, null when it is something else. */
export function parseLimit(text: string): number | undefined | null {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export type Selection =
  | { kind: "ids"; ids: string[] }
  | { kind: "limit"; limit: number }
  | { kind: "all" };

export function selectionOf(ids: string[], limit: number | undefined | null): Selection {
  if (ids.length > 0) return { kind: "ids", ids };
  if (typeof limit === "number") return { kind: "limit", limit };
  return { kind: "all" };
}

export function selectionBody(selection: Selection): { parseResultIds?: string[]; limit?: number } {
  if (selection.kind === "ids") return { parseResultIds: selection.ids };
  if (selection.kind === "limit") return { limit: selection.limit };
  return {};
}

export function describeScope(selection: Selection): string {
  if (selection.kind === "ids") {
    return `the ${selection.ids.length} listed parse result${selection.ids.length !== 1 ? "s" : ""}`;
  }
  if (selection.kind === "limit") return `up to ${selection.limit} eligible parse results`;
  return "every eligible parse result";
}

export function dryRunLabel(selection: Selection): string {
  if (selection.kind === "ids") {
    return `Dry run — preview ${pluralise(selection.ids.length, "document")}`;
  }
  if (selection.kind === "limit") return `Dry run — up to ${selection.limit}`;
  return "Dry run";
}

export function runBackfillLabel(selection: Selection): string {
  if (selection.kind === "ids") {
    return `Run backfill for ${pluralise(selection.ids.length, "document")}`;
  }
  if (selection.kind === "limit") return `Run backfill — oldest ${selection.limit}`;
  return "Run backfill — all eligible";
}

const DRY_RUN_COUNTS_ONLY = "A dry run only counts them; paste IDs to preview what each would do.";

export function scopeNote(selection: Selection): string {
  if (selection.kind === "ids") {
    return `Both buttons act on the ${pluralise(selection.ids.length, "pasted document")} only. A dry run writes nothing and its results appear under the Previews tab.`;
  }
  if (selection.kind === "limit") {
    return `Both buttons take the oldest ${selection.limit} eligible documents. ${DRY_RUN_COUNTS_ONLY}`;
  }
  return `Run backfill covers every eligible document in the publication. ${DRY_RUN_COUNTS_ONLY}`;
}

export function badgeLabel(selection: Selection): string | undefined {
  if (selection.kind === "ids") return pluralise(selection.ids.length, "ID");
  if (selection.kind === "limit") return `Limit ${selection.limit}`;
  return undefined;
}
